import { FC, useContext, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import {
  CollapsedLeftLogoIcon,
  ConsoleIcon,
  ShellIcon,
} from '@smartshell/ui-kit';

import { HostsDataResponse } from 'hooks/useHostsData/interfaces';
import { HOSTS_DATA } from 'hooks/useHostsData/schema';
import { LocalizationContext } from 'localization';
import {
  AUTHENTICATE_SHELL_CLIENT,
  GET_SHORTCUTS,
  IAuthenticateShellClientRequest,
  IAuthenticateShellClientResponse,
  IGetShortcutGroupsResponse,
  IGetShortcutsResponse,
  IShortcutGroup,
  SHORTCUT_GROUPS,
} from 'schemas';
import { SettingsContext } from 'settings/constants';

import { ShellAuthSidebar } from './ShellAuthSidebar';
import { ShellCatalogFilters } from './ShellCatalogFilters';
import s from './ShellDesktop.module.scss';
import { ShellRightSidebar } from './ShellRightSidebar';
import { ShellShortcutCatalog } from './ShellShortcutCatalog';
import { ShellSlideOutSidebar } from './ShellSlideOutSidebar';
import { ShellTaskbar } from './ShellTaskbar';
import { createShellTheme } from './shellTheme';
import {
  IShellTopPanelSession,
  ShellTopPanel,
  TShellTopPanelTab,
} from './ShellTopPanel';

export const SHELL_HOST_ID = 10;
export const SHELL_AUTH_CLIENT_UUID = 'preset:client:8';

type TShellPage = TShellTopPanelTab | 'tariffs';
type TShellSidebar = 'auth' | null;

export const ShellDesktop: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    state: { settings },
  } = useContext(SettingsContext);
  const [page, setPage] = useState<TShellPage>('games');
  const [sidebar, setSidebar] = useState<TShellSidebar>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [searchValue, setSearchValue] = useState('');
  // Prefer billing's existing result, but read the sandbox when it is not yet
  // cached (for example, when shell is opened before the hosts page).
  const { data } = useQuery<HostsDataResponse>(HOSTS_DATA, {
    fetchPolicy: 'cache-first',
  });
  const { data: shortcutGroupsData } = useQuery<IGetShortcutGroupsResponse>(
    SHORTCUT_GROUPS,
    { fetchPolicy: 'cache-first' }
  );
  const { data: shortcutsData } = useQuery<IGetShortcutsResponse>(
    GET_SHORTCUTS,
    { fetchPolicy: 'cache-first' }
  );
  const [authenticateShellClient, { loading: isAuthenticating }] = useMutation<
    IAuthenticateShellClientResponse,
    IAuthenticateShellClientRequest
  >(AUTHENTICATE_SHELL_CLIENT, {
    refetchQueries: [HOSTS_DATA],
    awaitRefetchQueries: true,
  });
  const host = data?.hostsOverview.find(({ id }) => id === SHELL_HOST_ID);
  const session = host?.client_sessions.find(Boolean);
  const sessionStatus: IShellTopPanelSession | undefined = session
    ? { id: session.id, timeLeft: session.time_left }
    : undefined;
  const catalogTab: TShellTopPanelTab =
    page === 'applications' ? 'applications' : 'games';
  const catalogGroups = useMemo<IShortcutGroup[]>(() => {
    const groupType = catalogTab === 'games' ? 'GAME' : 'APPLICATION';

    return [...(shortcutGroupsData?.shortcutGroups ?? [])]
      .filter((group) => group.groupType === groupType)
      .sort((left, right) => right.sort - left.sort);
  }, [catalogTab, shortcutGroupsData]);
  const theme = createShellTheme(settings);
  const hostGroupTitle =
    data?.hostGroupsOverview.find((group) => group.id === host?.group_id)
      ?.title ?? '';

  const changePage = (tab: TShellTopPanelTab): void => {
    setPage(tab);
    setSelectedGroupId(null);
    setSearchValue('');
  };

  const loginShellClient = async (): Promise<void> => {
    await authenticateShellClient({
      variables: {
        hostId: SHELL_HOST_ID,
        clientUuid: SHELL_AUTH_CLIENT_UUID,
      },
    });
    setSidebar(null);
  };

  return (
    <section
      className={s.ssShellDesktop}
      style={theme.style}
      aria-label={t('shell.client')}
      data-testid="ss-shell-desktop"
      data-shell-host-id={SHELL_HOST_ID}
      data-shell-host-online={String(host?.online ?? false)}
      data-shell-session-id={session?.id ?? ''}
      data-shell-page={page}
    >
      <ShellTopPanel
        hostAlias={host?.alias ?? `PC-${SHELL_HOST_ID}`}
        session={sessionStatus}
        t={t}
        onTabChange={changePage}
        onLoginClick={() => setSidebar('auth')}
        onTariffsClick={() => setPage('tariffs')}
      />
      <aside className={s.ssShellDesktopSidePanel}>
        <div className={s.ssShellDesktopSideTop}>
          <div className={s.ssShellDesktopLogo}>
            {theme.logoUrl ? (
              <img
                key={theme.logoUrl}
                src={theme.logoUrl}
                alt={host?.alias ?? `PC-${SHELL_HOST_ID}`}
              />
            ) : (
              <CollapsedLeftLogoIcon color="light" />
            )}
          </div>
        </div>
        <nav
          className={s.ssShellDesktopSideNavigation}
          aria-label="Разделы шелла"
        >
          <button
            className={s.ssShellDesktopSideNavigationItem}
            type="button"
            aria-label="Игры"
            aria-current="page"
          >
            <ConsoleIcon color="currentColor" size="l" />
          </button>
        </nav>
        <div className={s.ssShellDesktopSideVersion}>
          <ShellIcon color="currentColor" size="l" />
          <span>9.9.9.9</span>
        </div>
      </aside>
      <main className={s.ssShellDesktopContent} data-shell-page={page}>
        {page !== 'tariffs' && (
          <>
            <ShellCatalogFilters
              tab={catalogTab}
              groups={catalogGroups}
              selectedGroupId={selectedGroupId}
              searchValue={searchValue}
              t={t}
              onGroupChange={setSelectedGroupId}
              onSearchChange={setSearchValue}
            />
            <ShellShortcutCatalog
              tab={catalogTab}
              shortcuts={shortcutsData?.shortcuts ?? []}
              selectedGroupId={selectedGroupId}
              searchValue={searchValue}
              isGuest={!host?.user?.uuid}
              t={t}
            />
          </>
        )}
      </main>
      <aside className={s.ssShellDesktopWidgetPanel}>
        <ShellRightSidebar
          isGuest={!host?.user?.uuid}
          t={t}
          onLogin={() => setSidebar('auth')}
        />
      </aside>
      <ShellTaskbar className={s.ssShellDesktopBottomPanel} />
      <ShellSlideOutSidebar isOpen={sidebar === 'auth'}>
        <ShellAuthSidebar
          hostAlias={host?.alias ?? `PC-${SHELL_HOST_ID}`}
          hostGroupTitle={hostGroupTitle}
          isLoading={isAuthenticating}
          t={t}
          onClose={() => setSidebar(null)}
          onLogin={loginShellClient}
        />
      </ShellSlideOutSidebar>
    </section>
  );
};
